import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Divider,
  TextField,
  Grid,
  Snackbar,
  Alert,
  MenuItem,
  Chip,
} from "@mui/material";

import Navbar from "./AdminNav";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";
import PhoneNumberField from "../components/common/PhoneNumberField";

// ------------------------- VALIDATION HELPERS -------------------------
const validateURL = (url) => {
  if (!url) return true;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const validateName = (name) => {
  if (!name) return true;
  return /^[A-Za-z\s\-]+$/.test(name);
};

// Strips everything but digits and requires a minimum length so a
// country-code-only value (e.g. "+1") or a partial number can't pass.
const isValidPhone = (value = "") => {
  const digits = String(value).replace(/\D/g, "");
  return digits.length >= 7;
};

/* -------------------------------------------------------
   Role normaliser
   API returns "USER" → talent, "COMPANY" → director, "ADMIN" → admin
--------------------------------------------------------- */
const normaliseRole = (roleName = "") => {
  const r = roleName.toUpperCase();
  if (r === "USER") return "talent";
  if (r === "COMPANY") return "director";
  if (r === "ADMIN") return "admin";
  return "talent"; // safe default
};

/* -------------------------------------------------------
   Role badge colours
--------------------------------------------------------- */
const ROLE_CHIP = {
  talent:   { label: "Talent",   color: "primary" },
  director: { label: "Director", color: "secondary" },
  admin:    { label: "Admin",    color: "error" },
};

const UserDetailsPage = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read role from URL query param ?role=USER|COMPANY|ADMIN
  // Using query param (not router state) so it survives target="_blank" new-tab opens
  const roleFromParam = searchParams.get("role") || "";
  const role = normaliseRole(roleFromParam); // "talent" | "director" | "admin"

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirector, setIsDirector] = useState(false);

  const [phoneError, setPhoneError] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [errors, setErrors] = useState({
    FileURL: "",
    Name2: "",
    Name3: "",
  });

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [locations, setLocations] = useState([]);

  const [client, setClient] = useState({
    UserId: null,
    Password: null,
    ContactEmail: null,
    YourName: null,   // maps to "first name" for talent / "username" for others
    Name2: null,
    Name3: null,
    PhonNumber: "",
    CompanyName: null,
    Address1: null,
    Address2: null,
    City: null,
    State1: null,
    Logo: null,
    FileURL: null,
  });

  const [logoFile, setLogoFile] = useState(null);

  /* ----------------------------------------------------------
     INITIAL LOAD
  ---------------------------------------------------------- */
  useEffect(() => {
    if (email) {
      fetchClientDetails();
      if (role === "director") {
        checkIsDirector(email);
        fetchLocations();
      }
    }
  }, [email]);

  /* ----------------------------------------------------------
     FETCH CLIENT DETAILS
  ---------------------------------------------------------- */
  const fetchClientDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/NewCastingNotice/GetClientDetails", {
        params: { email },
      });

      const data = response.data?.data?.[0] || {};

      setClient({
        UserId: data.userId ?? null,
        Password: data.password ?? null,
        ContactEmail: data.contactEmail ?? email,
        YourName: data.loginName ?? null,
        Name2: data.firstName ?? null,
        Name3: data.lastName ?? null,
        PhonNumber: data.phoneNumber ?? "",
        CompanyName: data.companyName ?? null,
        Address1: data.address1 ?? null,
        Address2: data.address2 ?? null,
        City: data.city ?? null,
        State1: data.state1 ?? null,
        Logo: data.logo ?? null,
        FileURL: data.finalURL ?? null,

      });
    } catch (err) {
      console.error("Error fetching client:", err);
    }
    setLoading(false);
  };

  /* ----------------------------------------------------------
     FETCH LOCATIONS
  ---------------------------------------------------------- */
  const fetchLocations = async () => {
    try {
      const res = await api.get(ENDPOINTS.getLocations());
      const data = res.data?.data || [];

      const cleaned = data.filter(
        (loc) =>
          loc.locationCity &&
          loc.locationState &&
          loc.locationStateAbbreviatoion &&
          loc.locationCode
      );

      setLocations(cleaned);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  /* ----------------------------------------------------------
     CHECK IF DIRECTOR
  ---------------------------------------------------------- */
  const checkIsDirector = async (email) => {
    try {
      const response = await api.get(
        `/api/NewCastingNotice/GetNewCastingNoticeDetails?noticeId=0&page=1&pageSize=1&email=${encodeURIComponent(
          email
        )}`
      );
      if ((response.data?.data || []).length > 0) setIsDirector(true);
    } catch (err) {
      console.error("Director check failed:", err);
    }
  };

  /* ----------------------------------------------------------
     PHONE
  ---------------------------------------------------------- */
  const handlePhoneChange = (e) => {
    let value = e.target.value;

    if (!/^\d*$/.test(value)) return;
    if (value.length > 10) value = value.slice(0, 10);

    if (value !== "" && value.length !== 10) {
      setPhoneError("Phone must be exactly 10 digits.");
    } else {
      setPhoneError("");
    }

    setClient((prev) => ({ ...prev, PhonNumber: value }));
  };

  // Shared handler for the PhoneNumberField used by director/admin.
  // Validates on every change instead of unconditionally clearing the
  // error, so a country-code-only or too-short value stays flagged.
  const handlePhoneFieldChange = ({ phoneNumber }) => {
    setClient((prev) => ({
      ...prev,
      PhonNumber: phoneNumber,
    }));

    if (!phoneNumber) {
      setPhoneError("Phone number is required.");
    } else if (!isValidPhone(phoneNumber)) {
      setPhoneError("Enter a valid phone number.");
    } else {
      setPhoneError("");
    }
  };

  /* ----------------------------------------------------------
     FIELD CHANGE WITH VALIDATION
  ---------------------------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";

    if (name === "FileURL" && value && !validateURL(value))
      errorMsg = "Invalid URL";


    if (["Name2", "Name3"].includes(name)) {
      if (value && !validateName(value)) {
        errorMsg = "Only letters, spaces and hyphens allowed";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    setClient((prev) => ({ ...prev, [name]: value }));
  };

  /* ----------------------------------------------------------
     SAVE CLIENT DETAILS
  ---------------------------------------------------------- */
const handleUpdate = async () => {
  if (role !== "talent") {
    if (!client.PhonNumber || !isValidPhone(client.PhonNumber)) {
      setPhoneError(
        !client.PhonNumber
          ? "Phone number is required."
          : "Enter a valid phone number."
      );
      return setToast({
        open: true,
        message: "Enter a valid phone number.",
        severity: "error",
      });
    }
  }

  for (const field of ["Name2", "Name3"]) {
    if (!validateName(client[field])) {
      return setToast({
        open: true,
        message: `${field} contains invalid characters.`,
        severity: "error",
      });
    }
  }

  if (!validateURL(client.FileURL)) {
    return setToast({ open: true, message: "Invalid URL format.", severity: "error" });
  }

  const hasErrors = Object.values(errors).some((e) => e !== "");
  if (hasErrors) {
    return setToast({ open: true, message: "Fix all errors before saving.", severity: "error" });
  }

  setSaving(true);

  try {
    const formData = new FormData();

    // append everything except Logo and FileURL
    // append everything except Logo, FileURL, Zip, and YourName (username - not accepted by this endpoint)
    // Name2/Name3 are sent as FirstName/LastName since the API doesn't recognize Name2/Name3
    Object.entries(client).forEach(([k, v]) => {
      if (k === "Logo" || k === "FileURL" || k === "Zip" || k === "YourName") return;

      if (k === "Name2") {
        formData.append("FirstName", v ?? "");
      } else if (k === "Name3") {
        formData.append("LastName", v ?? "");
      } else {
        formData.append(k, v ?? "");
      }
    });

    // logo handling
    if (logoFile) {
      // new logo uploaded
      formData.append("Logo", logoFile);
    } else {
      // preserve existing logo
      formData.append("FileURL", client.Logo || client.FileURL || "");
    }

    const res = await api.post(
      "/api/NewCastingNotice/updateClientDetails",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    const ok =
      res.data?.status?.toLowerCase() === "ok" ||
      res.data?.status?.toLowerCase() === "success";

    setToast({
      open: true,
      message: ok ? "Details updated successfully." : "Failed to update.",
      severity: ok ? "success" : "error",
    });
  } catch (err) {
    console.error("Update error:", err);
  }

  setSaving(false);
};

  /* ----------------------------------------------------------
     LOGO UPLOAD HANDLER
  ---------------------------------------------------------- */
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setToast({ open: true, message: "Only JPG, PNG, or WEBP images are allowed.", severity: "error" });
      return;
    }

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setToast({ open: true, message: "Logo must be less than 20MB.", severity: "error" });
      return;
    }

    const getImageDimensions = (file) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.width, height: img.height }); };
        img.onerror = reject;
        img.src = url;
      });

    let dimensions;
    try {
      dimensions = await getImageDimensions(file);
    } catch {
      setToast({ open: true, message: "Invalid image file.", severity: "error" });
      return;
    }

    if (dimensions.width < 100 || dimensions.height < 100) {
      setToast({ open: true, message: "Logo must be at least 100×100 pixels.", severity: "error" });
      return;
    }

    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  /* ----------------------------------------------------------
     FIELD CONFIGS PER ROLE
     Each entry: { name, label, type, readOnly, custom }
  ---------------------------------------------------------- */

  // ---------- TALENT: username, first name, last name, password, email ----------
  const talentFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField label="User ID" value={client.UserId ?? ""} fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Email" value={client.ContactEmail ?? ""} fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          disabled
          label="Username (Login Name)"
          name="YourName"
          value={client.YourName ?? ""}
          onChange={handleChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Password"
          name="Password"
          value={client.Password ?? ""}
          onChange={handleChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="First Name"
          name="Name2"
          value={client.Name2 ?? ""}
          onChange={handleChange}
          fullWidth
          error={!!errors.Name2}
          helperText={errors.Name2}
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Last Name"
          name="Name3"
          value={client.Name3 ?? ""}
          onChange={handleChange}
          fullWidth
          error={!!errors.Name3}
          helperText={errors.Name3}
          InputProps={{ readOnly: true }}
        />
      </Grid>

    </Grid>
  );

  // ---------- DIRECTOR: email, username, phone, company, city, zipcode, logo ----------
  const directorFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField label="User ID" value={client.UserId ?? ""} fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Email" value={client.ContactEmail ?? ""} fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Username"
          name="YourName"
          value={client.YourName ?? ""}
          onChange={handleChange}
          fullWidth
          error={!!errors.YourName}
          helperText={errors.YourName}
          disabled
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Password"
          name="Password"
          value={client.Password ?? ""}
          onChange={handleChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PhoneNumberField
          phoneNumber={client.PhonNumber ?? ""}
          onChange={handlePhoneFieldChange}
          error={!!phoneError}
          helperText={phoneError}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Company Name"
          name="CompanyName"
          value={client.CompanyName ?? ""}
          onChange={handleChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          label="City, State"
          fullWidth
          value={client.City || ""}   // City holds locationCode, e.g. "NY1"
          onChange={(e) => {
            const selectedCode = e.target.value;
            const loc = locations.find((l) => l.locationCode === selectedCode);

            setClient((prev) => ({
              ...prev,
              City: loc?.locationCode || "",                 // e.g. "NY1"
              State1: loc?.locationStateAbbreviatoion || "",  // e.g. "NY"
            }));
          }}
        >
          {locations.map((loc) => (
            <MenuItem key={loc.locationCode} value={loc.locationCode}>
              {loc.locationCity} ({loc.locationStateAbbreviatoion})
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {/* Logo URL */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Logo URL"
          name="Logo"
          value={client.Logo ?? ""}
          onChange={handleChange}
          fullWidth
        />
      </Grid>

      {/* Upload Logo */}
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={500} mb={1}>Upload Logo (optional)</Typography>
        <input type="file" accept="image/*" onChange={handleLogoUpload} />
      </Grid>

      {(logoFile || client.Logo) && (
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" mb={1}>Logo Preview:</Typography>
          <img
            src={logoPreview || client.Logo}
            alt="Logo Preview"
            style={{ width: 120, height: "auto", borderRadius: 8, border: "1px solid #ddd", objectFit: "contain" }}
          />
        </Grid>
      )}
    </Grid>
  );

  // ---------- ADMIN: username, email, password, phone ----------
  const adminFields = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField label="User ID" value={client.UserId ?? ""} fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Email" value={client.ContactEmail ?? ""} fullWidth InputProps={{ readOnly: true }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Username"
          name="YourName"
          value={client.YourName ?? ""}
          onChange={handleChange}
          fullWidth
          error={!!errors.YourName}
          helperText={errors.YourName}
          disabled
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Password"
          name="Password"
          value={client.Password ?? ""}
          onChange={handleChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PhoneNumberField
          phoneNumber={client.PhonNumber ?? ""}
          onChange={handlePhoneFieldChange}
          error={!!phoneError}
          helperText={phoneError}
        />
      </Grid>
    </Grid>
  );

  const fieldsByRole = {
    talent:   talentFields,
    director: directorFields,
    admin:    adminFields,
  };

  const chipInfo = ROLE_CHIP[role];

  return (
    <>
      <Navbar />

      <div className="background-prop">
        <div style={{ maxWidth: "1200px", margin: "auto", padding: "20px" }}>
          <Box p={3}>
            {/* ---- HEADER ---- */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h4" fontWeight={600}>
                  User Details
                </Typography>
                {chipInfo && (
                  <Chip
                    label={chipInfo.label}
                    color={chipInfo.color}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  />
                )}
              </Box>

              <Box display="flex" gap={2}>
                {isDirector && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      window.open(`/admin/posted-notices/${email}`, "_blank")
                    }
                  >
                    View Posted Notices
                  </Button>
                )}
              </Box>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" height="200px" alignItems="center">
                <CircularProgress />
              </Box>
            ) : (
              <Card variant="outlined" sx={{ p: 3 }}>
                <CardContent>
                  {/* Render role-specific fields */}
                  {fieldsByRole[role]}

                  {/* Save Button */}
                  <Box mt={4} textAlign="center">
                    <Button
                      variant="contained"
                      color="success"
                      disabled={
                        saving ||
                        !!phoneError ||
                        Object.values(errors).some((e) => e !== "")
                      }
                      onClick={handleUpdate}
                    >
                      {saving ? "Saving…" : "Save Changes"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </div>
      </div>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
};

export default UserDetailsPage;